import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { MemberDto, PermissionName, RoleName, RolesResponse } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  currentUserId: string;
  onSessionLost: () => void;
}

export const ROLE_LABELS: Record<RoleName, string> = {
  owner: 'مالك',
  accountant: 'محاسب',
  clerk: 'مُدخل بيانات',
  viewer: 'مشاهد',
};

/** الصلاحيات بلغة المستخدم لا بأسمائها البرمجية. */
export const PERMISSION_LABELS: Record<PermissionName, { label: string; hint: string }> = {
  'company.read': { label: 'الاطلاع على الشركة', hint: 'يرى الشاشات والقوائم — أساس أى دور.' },
  'report.read': { label: 'التقارير', hint: 'الميزان والقوائم المالية وكشوف الحسابات والأعمار.' },
  'journal.post': {
    label: 'تسجيل المستندات',
    hint: 'فواتير وإيصالات ومدفوعات ومصروفات وتحويلات — البرنامج يبنى القيد.',
  },
  'journal.manual': {
    label: 'القيود اليدوية وإعداد الخزائن والمطابقة',
    hint: 'قيد يومية حر، خزائن وبنوك، مطابقة بنكية. تفرض التحقق بخطوتين.',
  },
  'period.close': {
    label: 'إقفال الفترات',
    hint: 'يمنع التسجيل فى شهر مقفل. تفرض التحقق بخطوتين.',
  },
  'period.reopen': {
    label: 'إعادة فتح الفترات',
    hint: 'تمس أرقامًا رآها أحد. تفرض التحقق بخطوتين.',
  },
  'user.manage': { label: 'إدارة المستخدمين والأدوار', hint: 'للمالك وحده.' },
  'settings.manage': {
    label: 'تغيير الإعدادات المحاسبية',
    hint: 'سياسات الشركة والضرائب. تفرض التحقق بخطوتين.',
  },
  'data.import': {
    label: 'ترحيل البيانات من برنامج سابق',
    hint: 'رفع ملفات Excel واعتماد الأرصدة الافتتاحية وعكسها. تفرض التحقق بخطوتين.',
  },
};

const ISSUE_LABELS: Record<string, string> = {
  LAST_OWNER: 'لا بد أن يبقى للشركة مالك واحد على الأقل',
  MEMBER_SELF_CHANGE: 'لا تغيّر دورك ولا تحذف نفسك — يعملها مالك آخر',
  CUSTOM_ROLE_UNKNOWN: 'الدور المخصص غير موجود',
};

function problemText(caught: ProblemError): string {
  const issues = caught.problem.issues ?? [];
  if (issues.length > 0) {
    return issues.map((issue) => ISSUE_LABELS[issue.code] ?? issue.message).join('، ');
  }
  return caught.problem.detail ?? caught.problem.title;
}

/** قيمة الاختيار الموحّد: دور جاهز `builtin:clerk` أو مخصص `custom:<id>` فوق دور أساس. */
function assignmentOf(member: Pick<MemberDto, 'role' | 'customRoleId'>): string {
  return member.customRoleId ? 'custom:' + member.customRoleId : 'builtin:' + member.role;
}

/**
 * الدور المخصص يُسند فوق «مُدخل بيانات» أساسًا: الأساس لا يمنح شيئًا بنفسه (الصلاحيات
 * من الدور المخصص)، ويبقى للعرض ولقرار التحقق بخطوتين حين لا يمنحه الدور المخصص.
 */
function parseAssignment(value: string): { role: RoleName; customRoleId: string | null } {
  if (value.startsWith('custom:')) return { role: 'clerk', customRoleId: value.slice(7) };
  return { role: value.slice(8) as RoleName, customRoleId: null };
}

/** المستخدمون والأدوار المخصصة — للمالك (`user.manage`). */
export default function Members({ companyId, currentUserId, onSessionLost }: Props) {
  const [members, setMembers] = useState<MemberDto[] | null>(null);
  const [roles, setRoles] = useState<RolesResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // إضافة عضو
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [assignment, setAssignment] = useState('builtin:clerk');

  // دور مخصص (جديد أو قيد التعديل)
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [roleName, setRoleName] = useState('');
  const [rolePermissionsDraft, setRolePermissionsDraft] = useState<Set<PermissionName>>(
    () => new Set(['company.read']),
  );

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      setFailure(
        caught instanceof ProblemError
          ? problemText(caught)
          : caught instanceof Error
            ? caught.message
            : String(caught),
      );
    },
    [onSessionLost],
  );

  const load = useCallback(() => {
    api.members(companyId).then(setMembers).catch(handleError);
    api.roles(companyId).then(setRoles).catch(handleError);
  }, [companyId, handleError]);

  useEffect(load, [load]);

  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      await action();
    } catch (caught) {
      handleError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function addMember(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      setMembers(
        await api.addMember(companyId, {
          email: email.trim(),
          displayName: displayName.trim(),
          ...(password ? { password } : {}),
          ...parseAssignment(assignment),
        }),
      );
      setEmail('');
      setDisplayName('');
      setPassword('');
    });
  }

  async function saveRole(event: FormEvent): Promise<void> {
    event.preventDefault();
    const body = {
      nameAr: roleName.trim(),
      permissions: [...rolePermissionsDraft].filter(
        (permission): permission is Exclude<PermissionName, 'user.manage'> =>
          permission !== 'user.manage',
      ),
    };
    await run(async () => {
      setRoles(
        editingRoleId
          ? await api.updateRole(companyId, editingRoleId, body)
          : await api.createRole(companyId, body),
      );
      setEditingRoleId(null);
      setRoleName('');
      setRolePermissionsDraft(new Set(['company.read']));
      api.members(companyId).then(setMembers).catch(handleError);
    });
  }

  const options = (
    <>
      <optgroup label="أدوار جاهزة">
        {(roles?.builtIn ?? []).map((item) => (
          <option key={item.role} value={'builtin:' + item.role}>
            {ROLE_LABELS[item.role]}
          </option>
        ))}
      </optgroup>
      {(roles?.custom.length ?? 0) > 0 && (
        <optgroup label="أدوار مخصصة">
          {roles?.custom.map((item) => (
            <option key={item.id} value={'custom:' + item.id}>
              {item.nameAr}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );

  return (
    <>
      {failure && (
        <div className="panel">
          <p className="error">{failure}</p>
        </div>
      )}

      <div className="panel">
        <h2>المستخدمون</h2>
        {members === null && !failure && <Loading />}
        {members && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>البريد</th>
                  <th>الدور</th>
                  <th>تحقق بخطوتين</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const self = member.userId === currentUserId;
                  return (
                    <tr key={member.userId}>
                      <td>
                        {member.displayName}
                        {self && <span className="label"> (أنت)</span>}
                      </td>
                      <td className="mono">{member.email}</td>
                      <td>
                        <select
                          value={assignmentOf(member)}
                          disabled={busy || self}
                          onChange={(event) =>
                            void run(async () => {
                              setMembers(
                                await api.updateMember(
                                  companyId,
                                  member.userId,
                                  parseAssignment(event.target.value),
                                ),
                              );
                            })
                          }
                        >
                          {options}
                        </select>
                      </td>
                      <td>
                        <span className={'pill' + (member.mfaEnrolled ? '' : ' bad')}>
                          {member.mfaEnrolled ? 'مفعّل' : 'غير مفعّل'}
                        </span>
                      </td>
                      <td>
                        {!self && (
                          <button
                            type="button"
                            className="ghost"
                            disabled={busy}
                            onClick={() => {
                              if (!window.confirm('سحب عضوية ' + member.displayName + '؟')) return;
                              void run(async () => {
                                setMembers(await api.removeMember(companyId, member.userId));
                              });
                            }}
                          >
                            سحب العضوية
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="sub">
          تغيير دور شخص يُخرجه من جلساته ليدخل بالصلاحيات الجديدة. سحب العضوية لا يمس قيوده ولا سجل
          تدقيقه.
        </p>
      </div>

      <form className="panel" onSubmit={(event) => void addMember(event)} noValidate>
        <h2>إضافة مستخدم</h2>
        <div className="form-row">
          <label className="grow">
            الاسم
            <input
              value={displayName}
              maxLength={200}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </label>
          <label className="grow">
            البريد
            <input
              type="email"
              dir="ltr"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label>
            الدور
            <select value={assignment} onChange={(event) => setAssignment(event.target.value)}>
              {options}
            </select>
            <Hint>يحدد ما يراه هذا الشخص وما يستطيع فعله فى البرنامج.</Hint>
          </label>
        </div>
        <div className="form-row">
          <label className="grow">
            كلمة سر أولى (١٢ محرفًا على الأقل)
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Hint>أعطها لصاحبها بطريقة آمنة (لا فى جروب)، ويستطيع تغييرها بعد أول دخول.</Hint>
          </label>
        </div>
        <p className="sub">
          سلّم كلمة السر للمستخدم بنفسك، وهو يغيّرها من «كلمة السر» بعد دخوله. لو البريد لمستخدم
          موجود عندك اترك كلمة السر فارغة.
        </p>
        <div className="form-actions">
          <button
            type="submit"
            className="primary"
            disabled={busy || !email.trim() || !displayName.trim()}
          >
            إضافة
          </button>
        </div>
      </form>

      <div className="panel">
        <h2>الأدوار</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>الدور</th>
                <th>الصلاحيات</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(roles?.builtIn ?? []).map((item) => (
                <tr key={item.role}>
                  <td>
                    {ROLE_LABELS[item.role]} <span className="label">جاهز</span>
                  </td>
                  <td>{item.permissions.map((p) => PERMISSION_LABELS[p].label).join('، ')}</td>
                  <td />
                </tr>
              ))}
              {(roles?.custom ?? []).map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.nameAr} <span className="label">مخصص • {item.memberCount} عضو</span>
                  </td>
                  <td>{item.permissions.map((p) => PERMISSION_LABELS[p].label).join('، ')}</td>
                  <td>
                    <div className="panel-actions">
                      <button
                        type="button"
                        className="ghost"
                        disabled={busy}
                        onClick={() => {
                          setEditingRoleId(item.id);
                          setRoleName(item.nameAr);
                          setRolePermissionsDraft(new Set(item.permissions));
                        }}
                      >
                        تعديل
                      </button>
                      <button
                        type="button"
                        className="ghost"
                        disabled={busy || item.memberCount > 0}
                        title={item.memberCount > 0 ? 'أعد إسناد أعضائه أولاً' : undefined}
                        onClick={() =>
                          void run(async () => {
                            setRoles(await api.deleteRole(companyId, item.id));
                          })
                        }
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <form className="panel" onSubmit={(event) => void saveRole(event)} noValidate>
        <h2>{editingRoleId ? 'تعديل دور مخصص' : 'دور مخصص جديد'}</h2>
        <div className="form-row">
          <label className="grow">
            اسم الدور (مثلاً: أمين خزينة)
            <input
              value={roleName}
              maxLength={100}
              onChange={(event) => setRoleName(event.target.value)}
            />
            <Hint>دور مخصص تختار صلاحياته بنفسك، لو الأدوار الجاهزة لا تناسب.</Hint>
          </label>
        </div>
        {(roles?.assignable ?? []).map((permission) => (
          <label key={permission} className="option">
            <input
              type="checkbox"
              checked={rolePermissionsDraft.has(permission)}
              disabled={permission === 'company.read'}
              onChange={(event) =>
                setRolePermissionsDraft((current) => {
                  const next = new Set(current);
                  if (event.target.checked) next.add(permission);
                  else next.delete(permission);
                  return next;
                })
              }
            />
            <span>
              <strong>{PERMISSION_LABELS[permission].label}</strong>
              <span className="label">{PERMISSION_LABELS[permission].hint}</span>
            </span>
          </label>
        ))}
        <div className="form-actions">
          <button type="submit" className="primary" disabled={busy || !roleName.trim()}>
            {editingRoleId ? 'حفظ التعديل' : 'إنشاء الدور'}
          </button>
          {editingRoleId && (
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setEditingRoleId(null);
                setRoleName('');
                setRolePermissionsDraft(new Set(['company.read']));
              }}
            >
              إلغاء
            </button>
          )}
        </div>
      </form>
    </>
  );
}
